/* eslint-disable import/no-extraneous-dependencies */
import { useState, useDeferredValue, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';

import { status } from 'constants/general';
import { entityProviderDataKeysEnum } from 'modules/categories/enums';
import Button from 'modules/common/components/Button';
import Popup from 'modules/common/components/Popup';
import ItemWithImageSearchField from 'modules/common/components/ItemWithImageSearchField';
import {
  fetchEntities,
  submitEntityProvider as submitEntityProviderAction,
  editEntityProvider as editEntityProviderAction,
} from 'modules/categories/categoriesSlice';

import { getEntityListForDropDown } from './helper';
import {
  PopupTitleSpan,
  PopupWrapper,
  PopupTitle,
  PopupTitleWrapper,
  Container,
  PopupTitleContainer,
  extendSearchDropDownStyle,
  extendDropDownListItem,
  extendLoaderStyle,
  ButtonsContainer,
  extendGrayButtonStyle,
  extendBlueButtonStyle,
  EntityWrapper,
  EntityInfo,
  EntityName,
  EntityImage,
  EntityDetails,
  EntityInfoLabel,
  EntityInfoValue,
} from './AddEntityDiscountModal.style';

const AddEntityDiscountModal = ({ entityProviderInfo, onClose }) => {
  const selectedProviderBenefits = {};
  const { providerKey, providerBenefits, providerName, providerPhoto } = entityProviderInfo;
  if (providerKey)
    providerBenefits.forEach(benefit => {
      const { benefitKey, benefitValue, discountTypeId } = benefit;
      // 1 is percentage
      const benefitValueStr = (discountTypeId === 1 && `${benefitValue}%`) || benefitValue;

      selectedProviderBenefits[benefitKey] = benefitValueStr;
    });

  const healthPlanCountryId = useSelector(
    state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
  );
  const entities = useSelector(state => state.categories.entities);
  const submitEntityProvider = useSelector(state => state.categories.submitEntityProvider);
  const editEntityProvider = useSelector(state => state.categories.editEntityProvider);

  const dispatch = useDispatch();

  const { healthPlanId } = useParams();

  const prevSubmitEntityProvider = useDeferredValue(submitEntityProvider.status);
  const prevEditEntityProvider = useDeferredValue(editEntityProvider.status);

  const [selectedEntity, setSelectedEntity] = useState({});
  const [selectedEntityInfo, setSelectedEntityInfo] = useState(() => {
    if (providerKey) return selectedProviderBenefits;
    return {
      [entityProviderDataKeysEnum.general]: '',
      [entityProviderDataKeysEnum.consultation]: '',
      [entityProviderDataKeysEnum.labs]: '',
      [entityProviderDataKeysEnum.scans]: '',
      [entityProviderDataKeysEnum.clinic]: '',
      [entityProviderDataKeysEnum.surgical]: '',
    };
  });

  const onClickClose = () => {
    setSelectedEntity({});
    onClose();
  };

  useEffect(() => {
    if (
      (submitEntityProvider.status === status.SUCCESS &&
        prevSubmitEntityProvider !== submitEntityProvider.status) ||
      (editEntityProvider.status === status.SUCCESS &&
        prevEditEntityProvider !== editEntityProvider.status)
    ) {
      onClickClose();
    }
  }, [submitEntityProvider.status, editEntityProvider.status]);

  const searchEntities = (keyWord, page, override) => {
    const data = {
      BranchStatusId: 5,
      PageSize: 10,
      CountryId: healthPlanCountryId,
      PageNum: page,
      SearchTerm: keyWord,
      PrefixTitleIds: [2, 3, 4, 7],
    };
    const body = {
      data,
      override,
    };

    dispatch(fetchEntities(body));
  };

  const handleTextFieldChange = (name, event) => {
    const { value } = event.target;

    setSelectedEntityInfo(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onSelectEntity = async entityInfo => {
    setSelectedEntity(entityInfo);
  };

  const onSave = () => {
    const data = {
      healthPlanId,
      benefits: Object.keys(selectedEntityInfo).map(benefitKey => {
        const benefitValue = selectedEntityInfo[benefitKey];
        let discountType = 2; // value

        let benefitValueNoSpaces = '';

        if (benefitValue) {
          benefitValueNoSpaces = benefitValue.toString().replace(/[\s]/g, '');
          if (benefitValueNoSpaces.includes('%')) discountType = 1; // percentage
        }

        return {
          benefitKey,
          benefitValue: (benefitValueNoSpaces && benefitValueNoSpaces.replace('%', '')) || 0,
          // 1 Precentage
          discountType,
        };
      }),
    };

    const payload = {
      data,
    };

    if (!providerKey) {
      const { entityKey, name, image } = selectedEntity;

      payload.data.providerTypeId = 6;
      payload.data.provider = {
        providerKey: entityKey,
        providerName: name,
        providerPhoto: image,
      };
    } else payload.providerKey = providerKey;

    const action = (providerKey && editEntityProviderAction) || submitEntityProviderAction;

    dispatch(action(payload));
  };

  const { entityKey, name, image } = selectedEntity;

  const isSubmitting =
    submitEntityProvider.status === status.SUBMITING ||
    editEntityProvider.status === status.SUBMITING;

  return (
    <Popup showCloseBtn={false}>
      <PopupWrapper>
        <PopupTitleContainer>
          <PopupTitleWrapper>
            <PopupTitle>{(providerKey && 'Edit entity') || 'Add entity'}</PopupTitle>
            <PopupTitleSpan
              onClick={() => {
                onClickClose();
              }}
            />
          </PopupTitleWrapper>
        </PopupTitleContainer>
        <Container smallHeight={!!providerKey}>
          <div
            style={{
              width: '796px',
              height: '520px',
              boxSizing: 'border-box',
            }}
          >
            {!providerKey && (
              <div
                style={{
                  padding: '16px',
                  background: '#F1F4F6',
                }}
              >
                <ItemWithImageSearchField
                  isRequired
                  isDynamic
                  isDropDown
                  searchable
                  key="doctors-search-list"
                  componentName="entities-search-list"
                  onTypingDelay={500}
                  placeHolder="Search for an entity"
                  fieldValue=""
                  options={getEntityListForDropDown(entities?.data, selectedEntity)}
                  onChanges={payload => {
                    onSelectEntity(payload?.extras);
                  }}
                  onSearch={(keyWord, pageNumber, override) => {
                    searchEntities(keyWord, pageNumber, override);
                  }}
                  language="en"
                  isLoading={entities.status === status.FETCHING}
                  extendLoaderStyle={extendLoaderStyle}
                  isValid
                  extendDropDownStyle={extendSearchDropDownStyle}
                  extendDropDownListItem={extendDropDownListItem}
                  icon="e905"
                  loadOnScroll
                  hideActionIcons
                />
              </div>
            )}
            {/** Added Doctors section */}
            <EntityWrapper>
              {(entityKey || providerKey) && (
                <>
                  <EntityInfo>
                    <EntityImage src={image || providerPhoto} />
                    <EntityName>{name || providerName}</EntityName>
                  </EntityInfo>
                  <EntityDetails>
                    <EntityInfoLabel>General</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.general]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.general, event)
                      }
                    />
                    <EntityInfoLabel>Consultation</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.consultation]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.consultation, event)
                      }
                    />
                    <EntityInfoLabel>Labs Analysis</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.labs]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.labs, event)
                      }
                    />
                    <EntityInfoLabel>Scan Service</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.scans]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.scans, event)
                      }
                    />
                    <EntityInfoLabel>In-Clinic</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.clinic]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.clinic, event)
                      }
                    />
                    <EntityInfoLabel>Surgical</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.surgical]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.surgical, event)
                      }
                    />
                    <EntityInfoLabel>Emergency</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.emergency]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.emergency, event)
                      }
                    />
                    <EntityInfoLabel>ICU</EntityInfoLabel>
                    <EntityInfoValue
                      value={selectedEntityInfo[entityProviderDataKeysEnum.icu]}
                      onChange={event =>
                        handleTextFieldChange(entityProviderDataKeysEnum.icu, event)
                      }
                    />
                  </EntityDetails>
                </>
              )}
            </EntityWrapper>
            <ButtonsContainer>
              <Button
                onClick={() => {
                  onClickClose();
                }}
                btnText="Cancel"
                extendButtonStyle={extendGrayButtonStyle}
                key="dismiss-popup"
              />
              <Button
                onClick={() => {
                  onSave();
                }}
                btnText={(providerKey && 'Edit') || 'Save'}
                extendButtonStyle={extendBlueButtonStyle}
                key="proceed-save-view"
                disabled={!providerKey && !selectedEntity.entityKey}
                isLoading={isSubmitting}
                extendLoaderStyle={extendLoaderStyle}
              />
            </ButtonsContainer>
          </div>
        </Container>
      </PopupWrapper>
    </Popup>
  );
};

AddEntityDiscountModal.propTypes = {
  onClose: PropTypes.func,
};

export default AddEntityDiscountModal;
